import {
  PRODUCT_OPTIONS_ENUMS,
  PRODUCT_TYPE_ENUMS,
} from '@/features/ProductDetails/constants/ProductType';
import {
  AddEditAddressUserDetails,
  CustomAttribute,
  UpdateBillingInfromationParams,
  UpdateShippingInfromationParams,
  UserDetails,
} from '@/models/user-details';
import { endpoints, http, logger, storage } from '@/config';
import {
  ADDRESS_ENDPOINT,
  IMAGE_URI,
  STATES_ENDPOINT,
} from '@/config/endpoints';
import { WishlistItemPropTypes } from '@/context/wishlistContext';
import {
  BASE_FE_URL,
  MENU_LINKS,
  OUT_OF_STOCK_CODE,
  PROUDCT_ATTRIBUTE_SETS_ENUMS,
  REVALIDATE_TIME,
} from '@/constants';
import { Product } from '@/models/product';
import { BannerImageTypes } from '@/models/global';
import { ProductItemPropTypes } from '@/components/Card/Product';
import { BentoBoxProductsType } from '@/app/(pages)/gifting/quartet-box/[urlKey]/page';
import { getExclusiveFilters } from './exclusiveFilters';

type DateFormats = 'DD-MM-YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD' | 'YYYY/MM/DD';

export const regex = {
  email: new RegExp('[a-z0-9]+@[a-z]+.[a-z]{2,3}'),
  mobilenumber: new RegExp('[0-9]{10}'),
  pincode: new RegExp('[0-9]{6}'),
};

export const isBrowser: boolean =
  typeof window !== 'undefined' && typeof window.document !== 'undefined';

export const blockInvalidChar = (e: React.KeyboardEvent) =>
  ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault();

export const sleep = (ms?: number) =>
  new Promise(resolve => setTimeout(resolve, ms ?? 1000));

export const getKeyValue = (data: any, attribute: string) => {
  return data?.reduce((acc: any, curr: any) => {
    acc = {
      ...acc,
      [curr[attribute]]: curr?.value,
    };
    return acc;
  }, {});
};

export const getFormattedAttributes = (data: any) =>
  data?.reduce((acc: any, curr: any) => {
    const { custom_attributes, ...rest } = curr;
    const attributes = getKeyValue(custom_attributes, 'attribute_code');
    acc.push({ ...rest, attributes });
    return acc;
  }, []);

export const getImages = (images: any, type?: 'image' | 'thumbnail') => {
  if (type) {
    return images.find((el: any) => el.types.includes(type));
  } else {
    return images.reduce(
      (acc: any, curr: any) => {
        if (curr.types.includes('image')) {
          acc.main = curr;
        } else {
          acc.other.push(curr);
        }
        return acc;
      },
      { main: {}, other: [] }
    );
  }
};

export const getItemsFromCart = (cart: any) => cart?.totals?.items;

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  timeout = 1000
) => {
  let timer: NodeJS.Timeout;

  const debouncedFn = (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), timeout);
  };

  debouncedFn.cancel = () => clearTimeout(timer);

  return debouncedFn;
};

export const convertDateFormat = (inputDate: string, format: DateFormats) => {
  if (!inputDate) {
    return null;
  }

  const hasTime = inputDate.includes(' ');
  if (hasTime) {
    inputDate = inputDate.split(' ')[0];
  }

  const [year, month, day] = inputDate.split(/[-/]/);
  const commonFormatDate = `${year}-${month.padStart(2, '0')}-${day.padStart(
    2,
    '0'
  )}`;

  const date = new Date(commonFormatDate);

  const formatDate = (date: Date, format: DateFormats): string => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString();

    switch (format) {
      case 'DD-MM-YYYY':
        return `${day}-${month}-${year}`;
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'YYYY/MM/DD':
        return `${year}/${month}/${day}`;
      default:
        throw new Error('Invalid date format');
    }
  };

  return formatDate(date, format);
};

export function findInProductOptions(title: string, options: any[]) {
  if (options && options.length > 0) {
    for (const option of options) {
      if (option?.title === title) {
        return option;
      }
    }
  }
  return false;
}

export const getBasicCartRequest = (product: any) => {
  const request: any = {
    ...product,
    qty: 1,
    size: {},
    gift: {},
    giftMessage: {},
  };
  if (
    product?.type_id === PRODUCT_TYPE_ENUMS.configurable &&
    product.extension_attributes?.configurable_product_options?.length > 0
  ) {
    request.size.option_id =
      product.extension_attributes?.configurable_product_options[0]?.attribute_id;
    request.size.option_value = product?.size;
  }
  const isGiftProductOptionPresent = findInProductOptions(
    PRODUCT_OPTIONS_ENUMS.gift,
    product?.options
  );
  if (isGiftProductOptionPresent) {
    request.gift.option_id = isGiftProductOptionPresent?.option_id;
    request.gift.option_value = '1';
  }
  const isGiftMessageOptionPresent = findInProductOptions(
    PRODUCT_OPTIONS_ENUMS.giftMessage,
    product?.options
  );
  if (isGiftMessageOptionPresent) {
    request.giftMessage.option_id = isGiftMessageOptionPresent?.option_id;
    request.giftMessage.option_value = JSON.stringify({
      index: 0,
      message: null,
      greeting_card: null,
    });
  }
  return request;
};

export const getOptionByValue = (
  options: { label: string; value: string }[],
  value: number | string
) => options?.find((el: any) => el.value === value?.toString());

export function formatDate(
  date: Date,
  format: 'DD/MM/YYYY' | 'YYYY-MM-DD' | 'DD MMM YYYY'
): string {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  const formattedDay = day < 10 ? `0${day}` : `${day}`;
  const formattedMonth = month < 10 ? `0${month}` : `${month}`;
  const formattedYear = `${year}`;

  switch (format) {
    case 'DD/MM/YYYY':
      return `${formattedDay}/${formattedMonth}/${formattedYear}`;
    case 'YYYY-MM-DD':
      return `${formattedYear}-${formattedMonth}-${formattedDay}`;
    case 'DD MMM YYYY':
      return `${formattedDay} ${date?.toLocaleString('default', {
        month: 'short',
      })} ${formattedYear}`;
    default:
      return date.toLocaleDateString();
  }
}

export const getLocalDate = (date: string, placeholder = '') => {
  if (!date) {
    return placeholder;
  }
  return new Date(date).toLocaleDateString();
};

export const getCartBasedCategories = (items: any) => {
  if (items?.length) {
    return items?.reduce(
      (acc: any, curr: any) => {
        if (
          curr?.extension_attributes?.is_gift_hamper === '1' ||
          curr?.extension_attributes?.is_greeting_card === 1 ||
          curr?.extension_attributes?.is_gift_box === '1' ||
          curr?.product_type === 'bundle'
        ) {
          acc.gifts.push(curr);
        } else {
          acc.groceries.push(curr);
        }
        return acc;
      },
      { groceries: [], gifts: [] }
    );
  }
  return null;
};

export const removeFromStorage = (keys: string[], session?: boolean) => {
  if (!session) {
    keys?.forEach((key: string) => {
      storage.remove(key);
    });
  } else {
    keys?.forEach((key: string) => {
      sessionStorage.removeItem(key);
    });
  }
};

export const checkItemInWishlist = (
  wishlistItems: WishlistItemPropTypes[],
  sku: string
) =>
  wishlistItems?.findIndex(
    ({ product_sku }) =>
      product_sku?.trim().toLowerCase() === sku?.trim().toLowerCase()
  ) > -1;

export const getValueFromAttributes = ({
  custom_attributes,
  code,
}: {
  custom_attributes: CustomAttribute[];
  code: string;
}) => custom_attributes?.find((item: any) => item.attribute_code === code);

export const getThumbnail = ({
  custom_attributes,
  portrait,
}: {
  custom_attributes: CustomAttribute[];
  portrait?: boolean;
}) => {
  const details = getValueFromAttributes({
    custom_attributes,
    code: portrait ? 'portrait_thumbnail' : 'thumbnail',
  });
  if (details) {
    return `${process.env.NEXT_PUBLIC_CDN_LINK}${IMAGE_URI}${details?.value}`;
  }
  return '';
};

export const extractContent = (input: string): string[] => {
  const pattern = /\{\{(.*?)\}\}/g;
  const matches = input?.match(pattern);

  if (matches) {
    return matches.map(match => match.slice(2, -2));
  }

  return [];
};

export const isDecimal = (value: any): boolean =>
  value - Math.floor(value) !== 0;

// This function takes a string and return the float number to a fixed digit or a whole number
export const convertToDecimal = (
  value: string | undefined,
  decimal = 2
): number | string => {
  if (!value) {
    return 0;
  }
  const numericValue = Number(value);
  if (isNaN(numericValue)) {
    return 0;
  }
  return isDecimal(numericValue)
    ? numericValue?.toFixed(decimal)
    : numericValue;
};

export type CurrencyFormatPropType = {
  value: string | number | undefined;
  format?: string;
  currency?: string;
  maximumFractionDigits?: number;
};

export const getCurrencyFormat = ({
  value,
  format = 'en-IN',
  currency = 'INR',
  maximumFractionDigits = isDecimal(value) ? 2 : 0,
}: CurrencyFormatPropType) =>
  value?.toLocaleString(format, {
    maximumFractionDigits,
    style: 'currency',
    currency,
  });

export const isGiftHamper = (item: Product) => {
  if (item?.extension_attributes?.attribute_set_name === 'gift hampers')
    return true;
  return false;
};
interface CategoryItem {
  id: number;
  custom_attributes: Array<{ attribute_code: string; value: string }>;
}

export function getUrlKey(
  category: CategoryItem | CategoryItem[],
  id?: number
): string | undefined {
  if (Array.isArray(category)) {
    const categoryObject = category.find(item => item.id === id);
    if (categoryObject) {
      const urlKeyAttribute = categoryObject.custom_attributes.find(
        attr => attr.attribute_code === 'url_key'
      );
      return urlKeyAttribute ? urlKeyAttribute.value : undefined;
    }
  } else {
    const urlKeyAttribute = category.custom_attributes.find(
      attr => attr.attribute_code === 'url_key'
    );
    return urlKeyAttribute ? urlKeyAttribute.value : undefined;
  }
  return undefined;
}

export function generateUrlKey(inputString: string): string {
  // Convert input string to lowercase
  let urlKey = inputString.toLowerCase();

  // Replace spaces with hyphens
  urlKey = urlKey.replace(/\s+/g, '-');

  // Replace characters other than underscore, hyphen, alphabets, and numbers with an empty string
  urlKey = urlKey.replace(/[^a-z0-9_-]/g, '');

  // Remove any consecutive hyphens
  urlKey = urlKey.replace(/-{2,}/g, '-');

  // Remove leading and trailing hyphens
  urlKey = urlKey.replace(/^-+|-+$/g, '');

  return urlKey;
}

export const findLifeStyleByName = (
  name: string,
  list: { label: string; value: number }[]
) => list?.find(({ label }) => name.toLowerCase() === label.toLowerCase());

export const getSiteMapCategoriesGraphql = (id = 3) => {
  return `{
  categoryList(filters: {ids: {eq: "${id}"}}) {
    children_count
    children {
      id
      name
      url_key
      children {
        id
        name
        url_key
        children {
            id
            name
            url_key
            children {
                id
                name
                url_key
          } 
        }
      }
    }
  }
}`;
};

export const getCategoriesGraphQl = (id = 3) => {
  return `{
    categoryList(filters: {ids: {eq: "${id}"}}) {
      children_count
      children {
        id
        name
        url_key
        children_count
        image
        banner1_image
        banner1_url
        banner1_title
        banner1_urltext
        children {
          id
          name
          url_key
          children_count
          image
          children {
              id
              name
              url_key
              children_count
              image
              children {
                id
                name
                url_key
                children_count
                image
            }
          }
        }
      }
    }
  }`;
};

export const getGiftingCategoriesGraphQl = (id = 1685) => {
  return `{
    categoryList(filters: {ids: {eq: "${id}"}}) {
      url_key
      children_count
      children {
        id
        name
        url_key
        banner1_image
        banner1_image_mobile
        redirect_url
      }
    }
  }`;
};

export const getCategoriesChildrenAndDataGraphQl = (id = 1685) => {
  return `{
    categoryList(filters: {ids: {eq: "${id}"}}) {
      id
      name
      url_key
      sub_title
      children {
        id
        name
        url_key
        image
        children {
          id
          name
          url_key
          image
        }
      }
    }
  }`;
};

export const getCategoryByIdGraphQl = (id: number) => {
  return `{
    categoryList(filters: {ids: {eq: "${id}"}}) {
      id
      name
      url_key
      banner1_image
      banner1_image_mobile
      redirect_url
    }
  }`;
};

export const getProductsStockGraphQl = () => {
  return `query GetProductStock($skus: [String]) {
     products(filter: {sku: {in: $skus}}, pageSize:500) {
        total_count,
        items {
            sku
            stock_status
        }
    }
}`;
};

export const getUrlParamsStr = (data: any) => {
  const searchParams = new URLSearchParams(data);
  return searchParams.toString();
};

export const getArticlePath = (type: string) => {
  switch (type) {
    case PROUDCT_ATTRIBUTE_SETS_ENUMS.storiesArticle:
      return `${MENU_LINKS.STORIES}/article`;
    case PROUDCT_ATTRIBUTE_SETS_ENUMS.storiesListicle:
      return `${MENU_LINKS.STORIES}/listicle`;
    case PROUDCT_ATTRIBUTE_SETS_ENUMS.storiesHierarchical:
      return `${MENU_LINKS.STORIES}/hierarchical`;
    case PROUDCT_ATTRIBUTE_SETS_ENUMS.storiesHierarchicalChild:
      return `${MENU_LINKS.STORIES}/hierarchical`;
    case PROUDCT_ATTRIBUTE_SETS_ENUMS.recipes:
      return `/recipes`;
    default:
      return '';
  }
};

export const getArticleTypeName = (type: string) => {
  switch (type) {
    case PROUDCT_ATTRIBUTE_SETS_ENUMS.storiesArticle:
    case PROUDCT_ATTRIBUTE_SETS_ENUMS.storiesListicle:
    case PROUDCT_ATTRIBUTE_SETS_ENUMS.storiesHierarchical:
    case PROUDCT_ATTRIBUTE_SETS_ENUMS.storiesHierarchicalChild:
      return `Article`;
    case PROUDCT_ATTRIBUTE_SETS_ENUMS.recipes:
      return `Recipe`;
    default:
      return '';
  }
};

export const filterImagesKeyBased = ({
  images,
  key,
  type,
}: {
  images: BannerImageTypes[];
  key: string;
  type?: 'image' | 'video';
}) => {
  return images?.reduce((acc: any, curr: BannerImageTypes) => {
    if (type) {
      if (curr.type === type && curr.code === key) {
        if (curr?.path) {
          acc.push({
            ...curr,
            updatedPath: `${process.env.NEXT_PUBLIC_CDN_LINK}${endpoints.PAGE_BANNER_PATH}${curr?.path}`,
            mobileUpdatedPath: curr?.mobile_path
              ? `${process.env.NEXT_PUBLIC_CDN_LINK}${endpoints.PAGE_BANNER_PATH}${curr?.mobile_path}`
              : `${process.env.NEXT_PUBLIC_CDN_LINK}${endpoints.PAGE_BANNER_PATH}${curr?.path}`,
          });
        }
      }
      return acc;
    } else {
      if (curr.code === key) {
        if (curr?.path) {
          acc.push({
            ...curr,
            updatedPath: `${process.env.NEXT_PUBLIC_CDN_LINK}${endpoints.PAGE_BANNER_PATH}${curr?.path}`,
            mobileUpdatedPath: curr?.mobile_path
              ? `${process.env.NEXT_PUBLIC_CDN_LINK}${endpoints.PAGE_BANNER_PATH}${curr?.mobile_path}`
              : `${process.env.NEXT_PUBLIC_CDN_LINK}${endpoints.PAGE_BANNER_PATH}${curr?.path}`,
          });
        }
      }
      return acc;
    }
  }, []);
};

export type RequestAttributeProps = {
  filterCategories: string | string[];
  filterLifestyle: string | string[];
  filterSkills?: string | string[];
  filterCuisine?: string | string[];
  filterAdditional?: string | string[];
  filterTexture?: string | string[];
  filterFlavour?: string | string[];
};

export function createRequestAttributes({
  filterCategories,
  filterLifestyle,
  filterCuisine,
  filterSkills,
  filterAdditional,
  filterTexture,
  filterFlavour,
}: RequestAttributeProps): string {
  const requestAttributes: {
    literals: {
      incategoriesnames?: Record<string, boolean>;
      lifestyle?: Record<string, boolean>;
      skillleveltags?: Record<string, boolean>;
      cuisine?: Record<string, boolean>;
      additionalfilters?: Record<string, boolean>;
      texture?: Record<string, boolean>;
      flavour?: Record<string, boolean>;
    };
  } = {
    literals: {},
  };

  if (filterCategories) {
    requestAttributes.literals.incategoriesnames =
      requestAttributes.literals.incategoriesnames || {};

    if (Array.isArray(filterCategories)) {
      filterCategories.forEach(category => {
        // @ts-expect-error its not undefined
        requestAttributes.literals.incategoriesnames[category] = true;
      });
    } else {
      requestAttributes.literals.incategoriesnames[filterCategories] = true;
    }
  }

  if (filterSkills) {
    requestAttributes.literals.skillleveltags =
      requestAttributes.literals.skillleveltags || {};

    if (Array.isArray(filterSkills)) {
      filterSkills.forEach(skill => {
        // @ts-expect-error its not undefined
        requestAttributes.literals.skillleveltags[skill] = true;
      });
    } else {
      requestAttributes.literals.skillleveltags[filterSkills] = true;
    }
  }

  if (filterCuisine) {
    requestAttributes.literals.cuisine =
      requestAttributes.literals.cuisine || {};

    if (Array.isArray(filterCuisine)) {
      filterCuisine.forEach(cuisine => {
        // @ts-expect-error its not undefined
        requestAttributes.literals.cuisine[cuisine] = true;
      });
    } else {
      requestAttributes.literals.cuisine[filterCuisine] = true;
    }
  }

  if (filterLifestyle) {
    requestAttributes.literals.lifestyle =
      requestAttributes.literals.lifestyle || {};

    if (Array.isArray(filterLifestyle)) {
      filterLifestyle.forEach(lifestyle => {
        // @ts-expect-error its not undefined
        requestAttributes.literals.lifestyle[lifestyle] = true;
      });
    } else {
      requestAttributes.literals.lifestyle[filterLifestyle] = true;
    }
  }

  if (filterAdditional) {
    requestAttributes.literals.additionalfilters =
      requestAttributes.literals.additionalfilters || {};

    if (Array.isArray(filterAdditional)) {
      filterAdditional.forEach(additionalFilter => {
        // @ts-expect-error its not undefined
        requestAttributes.literals.additionalfilters[additionalFilter] = true;
      });
    } else {
      requestAttributes.literals.additionalfilters[filterAdditional] = true;
    }
  }

  if (filterTexture) {
    requestAttributes.literals.texture =
      requestAttributes.literals.texture || {};

    if (Array.isArray(filterTexture)) {
      filterTexture.forEach(texture => {
        // @ts-expect-error its not undefined
        requestAttributes.literals.texture[texture] = true;
      });
    } else {
      requestAttributes.literals.texture[filterTexture] = true;
    }
  }

  if (filterFlavour) {
    requestAttributes.literals.flavour =
      requestAttributes.literals.flavour || {};

    if (Array.isArray(filterFlavour)) {
      filterFlavour.forEach(flavour => {
        // @ts-expect-error its not undefined
        requestAttributes.literals.flavour[flavour] = true;
      });
    } else {
      requestAttributes.literals.flavour[filterFlavour] = true;
    }
  }

  if (
    !requestAttributes.literals.incategoriesnames ||
    Object.keys(requestAttributes.literals.incategoriesnames).length === 0
  ) {
    delete requestAttributes.literals.incategoriesnames;
  }
  if (
    !requestAttributes.literals.skillleveltags ||
    Object.keys(requestAttributes.literals.skillleveltags).length === 0
  ) {
    delete requestAttributes.literals.skillleveltags;
  }
  if (
    !requestAttributes.literals.cuisine ||
    Object.keys(requestAttributes.literals.cuisine).length === 0
  ) {
    delete requestAttributes.literals.cuisine;
  }
  if (
    !requestAttributes.literals.lifestyle ||
    Object.keys(requestAttributes.literals.lifestyle).length === 0
  ) {
    delete requestAttributes.literals.lifestyle;
  }
  if (
    !requestAttributes.literals.additionalfilters ||
    Object.keys(requestAttributes.literals.additionalfilters).length === 0
  ) {
    delete requestAttributes.literals.additionalfilters;
  }
  if (
    !requestAttributes.literals.texture ||
    Object.keys(requestAttributes.literals.texture).length === 0
  ) {
    delete requestAttributes.literals.texture;
  }
  if (
    !requestAttributes.literals.flavour ||
    Object.keys(requestAttributes.literals.flavour).length === 0
  ) {
    delete requestAttributes.literals.flavour;
  }

  return JSON.stringify(requestAttributes);
}

export const countAvailableItems = (items: ProductItemPropTypes[]) => {
  return items?.reduce((acc, item) => {
    return item?.extension_attributes?.in_stock === OUT_OF_STOCK_CODE
      ? 0
      : acc + 1;
  }, 0);
};
export function isStringifiedJSON(input: string): boolean {
  try {
    const parsed = JSON.parse(input);
    return typeof parsed === 'object' && parsed !== null;
  } catch (error) {
    logger.error(error);
    return false;
  }
}

export function getEventStartEndTime(startTime: string, endTime: string) {
  const startDate = new Date(startTime);
  const endDate = new Date(endTime);

  const localStartDate = new Date(
    startDate.getTime() - startDate.getTimezoneOffset() * 60000
  );
  const localEndDate = new Date(
    endDate.getTime() - endDate.getTimezoneOffset() * 60000
  );

  const startDateOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };

  const formattedStartTime = localStartDate
    .toLocaleString('en-IN', startDateOptions as Intl.DateTimeFormatOptions)
    .replace(
      /(\d+:\d+\s*)([apAP][mM])/,
      (_, time, period) => `${time}${period.toUpperCase()}`
    );

  const endDateOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };

  const formattedEndTime = localEndDate
    .toLocaleString('en-US', endDateOptions as Intl.DateTimeFormatOptions)
    .replace(
      /(\d+:\d+\s*)([apAP][mM])/,
      (_, time, period) => `${time}${period.toUpperCase()}`
    );

  const formattedString = `${formattedStartTime} - ${formattedEndTime}`.replace(
    ' at ',
    ', '
  );

  return formattedString;
}

export function capitalizeWords(sentence: string): string {
  const cleanedSentence = sentence.replace(/[_-]/g, ' ');

  const words = cleanedSentence.split(' ');

  if (words.length > 1) {
    const capitalizedWords = words.map(word => capitalizeFirstLetter(word));
    return capitalizedWords.join(' ');
  } else {
    return capitalizeFirstLetter(cleanedSentence);
  }
}

function capitalizeFirstLetter(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export const getTotalCartValue = (
  products: ProductItemPropTypes[] | null
): number => {
  if (products && products?.length > 0) {
    return products?.reduce(
      (total, product) => total + (product.price || 0),
      0
    );
  }
  return 0;
};

export function getCartSkus(products: ProductItemPropTypes[] | null): string {
  if (products) {
    const skus: string[] = products.map(product => product.sku);
    return skus.join(', ');
  }
  return '';
}

// this function will return the page type based on the pathname
export const getPageType = (pathname: string) => {
  const path = pathname.split('/');
  return path[1];
};

export function generateRandomId() {
  const characters = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const idLength = 24;

  let randomId = '';
  for (let i = 0; i < idLength; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    randomId += characters.charAt(randomIndex);
  }

  randomId =
    randomId.substring(0, 4) +
    '-' +
    randomId.substring(4, 8) +
    '-' +
    randomId.substring(8, 12) +
    '-' +
    randomId.substring(12, 16) +
    '-' +
    randomId.substring(16);

  return randomId;
}

export function replaceUrl(url: string) {
  if (typeof window === 'object') {
    window.history.replaceState(
      {
        ...window.history.state,
        as: url,
        url: url,
      },
      '',
      url
    );
  }
}

export function getStorySchema(data: any) {
  const BASE_CDN = `${process.env.NEXT_PUBLIC_CDN_LINK}/media/catalog/product`;
  const attributes = getKeyValue(data?.custom_attributes, 'attribute_code');

  // Combine all article content for structured data (for crawlers like ChatGPT)
  const combineArticleContent = () => {
    const contentParts: string[] = [];

    // Add short_description if it exists
    if (attributes?.short_description) {
      contentParts.push(attributes.short_description);
    }

    // Add all description sections (description1 through description15)
    for (let i = 1; i <= 15; i++) {
      const descKey = `description${i}`;
      if (attributes?.[descKey] && attributes[descKey].trim()) {
        contentParts.push(attributes[descKey]);
      }
    }

    // Combine all content and clean up data attributes that aren't needed for crawlers
    const combinedContent = contentParts
      .filter(Boolean)
      .join(' ')
      // Remove data-start and data-end attributes (used for audio/video sync, not needed for SEO)
      .replace(/\s*data-start="[^"]*"/g, '')
      .replace(/\s*data-end="[^"]*"/g, '')
      .trim();

    return combinedContent || null;
  };

  const articleBody = combineArticleContent();

  const jsonLd: any = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': data?.sku,
    },
    headline: data?.name,
    description: attributes?.meta_description,
    image: `${BASE_CDN}${attributes?.thumbnail}`,
    author: {
      '@type': 'Organization',
      name: attributes?.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Food Stories',
      logo: {
        '@type': 'ImageObject',
        url: '../../public/images/global/Black_FS_Logo.png',
      },
    },
    datePublished: data?.created_at,
    dateModified: data?.updated_at,
  };

  // Add articleBody to structured data so crawlers can read the full content
  if (articleBody) {
    jsonLd.articleBody = articleBody;
  }

  return jsonLd;
}

export function durationStringToIso8601(duration: string): string {
  const match = duration.match(/(\d+)(?:\s*([hm]))?/gi);

  if (!match) {
    return duration; // Return null for invalid formats
  }

  let hours = 0;
  let minutes = 0;

  for (const part of match) {
    const value = parseInt(part.match(/^\d+/)![0], 10); // Extract number

    const unit = part.match(/[hm]$/) ? part.match(/[hm]$/)![0] : ''; // Extract unit (h or m)

    if (unit === 'h') {
      hours = value;
    } else if (unit === 'm') {
      minutes = value;
    } else {
      // This shouldn't happen due to the regex match
      throw new Error(`Unexpected unit in duration: ${duration}`);
    }
  }

  return `PT${hours.toString().padStart(2, '0')}H${minutes
    .toString()
    .padStart(2, '0')}M`;
}

export function htmlListToHowToSteps(
  htmlString: string
): { '@type': string; text: string }[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  const steps = Array.from(doc.querySelectorAll('li'))

    .filter(element => element.textContent!.trim() !== '')
    .map(element => ({
      '@type': 'HowToStep',

      text: element.textContent!.trim(),
    }));

  return steps;
}

export function generateProductList(items: any[]): string | undefined {
  let productList = 'Checkout these products from my Foodstories shelf:\n\n';
  if (!items || items.length === 0) return;
  items.forEach(item => {
    const urlParams =
      item.product_attribute_set === 'default' && !item.parent_url_key
        ? `shop/product/${item.url_key}`
        : item.product_attribute_set === 'default' && item.parent_url_key
          ? `shop/product/${item.parent_url_key}?selected=${item?.product_sku}`
          : item.product_attribute_set === 'recipes'
            ? `recipes/${item?.product_sku}`
            : item?.product_attribute_set === 'stories-article'
              ? `stories/article/${item?.product_sku}`
              : item?.product_attribute_set === 'stories-listicle'
                ? `stories/listicle/${item?.product_sku}`
                : `stories/hierarchical/${item?.product_sku}`;
    const url = `${BASE_FE_URL}/${urlParams}`;
    productList += `${item.product_name}\n${url}\n\n`;
  });

  return productList;
}

export function convertSearchParamsToURL(
  searchParams: Record<string, string | string[]>
): string {
  let searchParamsString = '';

  Object.keys(searchParams).forEach(key => {
    const value = searchParams[key];
    if (Array.isArray(value)) {
      value.forEach(item => {
        searchParamsString += `${key}=${encodeURIComponent(item)}&`;
      });
    } else {
      searchParamsString += `${key}=${encodeURIComponent(value)}&`;
    }
  });

  // Remove the trailing '&' character
  searchParamsString = searchParamsString.slice(0, -1);

  return searchParamsString;
}

type ConfigurableOption = {
  label: string;
  value: string;
};

type ResponseItem = {
  sku: string;
  priceVariant: ConfigurableOption;
  designVariant: ConfigurableOption;
  image: string;
  name: string;
};

export function generateListMultiConfigurable(
  configurableBaseOptionsPrice: {
    defaultValue: string;
    options: ConfigurableOption[];
  },
  configurableBaseOptionsDesign: {
    defaultValue: string;
    options: ConfigurableOption[];
  },
  products: Product[]
): ResponseItem[] {
  const response: ResponseItem[] = [];

  products.forEach(product => {
    const priceVariantValue = product.custom_attributes.find(
      attr => attr.attribute_code === 'price_variant'
    )?.value;
    const designVariantValue = product.custom_attributes.find(
      attr => attr.attribute_code === 'design_variant'
    )?.value;
    const imageValue = product.custom_attributes.find(
      attr => attr.attribute_code === 'image'
    )?.value;

    if (priceVariantValue && designVariantValue && imageValue) {
      const priceVariant = configurableBaseOptionsPrice.options.find(
        option => option.value === priceVariantValue
      );
      const designVariant = configurableBaseOptionsDesign.options.find(
        option => option.value === designVariantValue
      );

      if (priceVariant && designVariant) {
        const item: ResponseItem = {
          sku: product.sku,
          priceVariant,
          designVariant,
          image: `${process.env.NEXT_PUBLIC_CDN_LINK}${IMAGE_URI}${imageValue}`,
          name: product.name,
        };
        response.push(item);
      }
    }
  });

  return response;
}

export function googleDataLayerEventPush({
  eventName,
  eventParameters,
}: {
  eventName: string;
  eventParameters: {
    item_list_id?: string;
    item_list_name?: string;
    eventSource?:
      | 'ARGOID_CART_RECOMMENDATIONS'
      | 'ARGOID_PLP'
      | 'ARGOID_SEARCH_DIRECT'
      | 'ARGOID_SEARCH_AUTOCOMPLETE'
      | 'ARGOID_HOME_TRENDING'
      | 'ARGOID_PDP_SIMILAR_PRODUCTS'
      | 'ARGOID_PDP_COMPLEMENTING_PRODUCTS';
    currency?: 'INR';
    value?: string | number;
    items: any[];
  };
}) {
  const itemsToSendInDataLayer = [];
  const items = eventParameters?.items;
  if (items && items.length > 0) {
    for (let i = 0; i < items.length; i++) {
      const newItem: any = {
        item_id: items[i].sku,
        item_name: items[i].name,
        affiliation: 'Google Merchandise Store',
        index: i,
        item_brand: 'Foodstories',
        item_category: 'Food',
        item_variant: `${items[i]?.weight} ${items[i]?.weight_unit ?? ''}`,
        price: items[i].price,
        quantity: 1,
      };
      if (eventParameters.item_list_id) {
        newItem.item_list_id = eventParameters.item_list_id;
        newItem.item_list_name = eventParameters.item_list_name;
      }
      itemsToSendInDataLayer.push(newItem);
    }
  }

  const dataLayer = window.dataLayer || [];
  dataLayer.push({ ecommerce: null });
  dataLayer.push({
    event: eventName,
    ecommerce: { ...eventParameters, items: itemsToSendInDataLayer },
  });
}

export function pushGA4Event(eventName: string, params?: Record<string, any>) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const dataLayer = (window as any).dataLayer || [];
    dataLayer.push({
      event: eventName,
      ...(params || {}),
    });
  } catch (e) {
    logger.error(`GA4 ${eventName} event failed`, e);
  }
}

/**
 * GA4 User Identity (purchase) — minimal implementation matching existing codebase style.
 *
 * Emits:
 * - first_purchase OR repeat_purchase (with transaction_id)
 * - user_type_identified (repeat) when user_id is available
 *
 * Deduped per session by transaction_id to avoid re-render double fires.
 */
export function trackGA4UserIdentityPurchase(
  transactionId: string | number | undefined,
  explicitUserId?: string | number
) {
  if (typeof window === 'undefined') return;
  if (transactionId === undefined || transactionId === null) return;

  const txn = String(transactionId);
  if (!txn) return;

  try {
    const lastTxn = sessionStorage.getItem('ga4_last_purchase_transaction_id');
    if (lastTxn === txn) return;
    sessionStorage.setItem('ga4_last_purchase_transaction_id', txn);

    const hasPurchased = localStorage.getItem('ga4_has_purchased') === '1';
    pushGA4Event(hasPurchased ? 'repeat_purchase' : 'first_purchase', {
      transaction_id: txn,
    });
    localStorage.setItem('ga4_has_purchased', '1');

    const user_id =
      explicitUserId !== undefined && explicitUserId !== null
        ? String(explicitUserId)
        : localStorage.getItem('user-id') || '';

    if (user_id) {
      pushGA4Event('user_type_identified', {
        user_id,
        user_type: 'repeat',
      });
    }
  } catch (e) {
    logger.error('GA4 user identity purchase tracking failed', e);
  }
}

export function getWeightWithUnit(
  weight?: string | number,
  weight_unit?: string | 'Serves'
) {
  // Convert to float
  let weightValue = parseFloat(String(weight));

  if (isNaN(weightValue) || !weight_unit) {
    return '';
  }

  // Convert to string and check decimal places
  const weightStr = weightValue.toString();
  const decimalPart = weightStr.split('.')[1];

  // If decimal exists and has 2 or more digits, round to 1 decimal place
  if (decimalPart && decimalPart.length > 1) {
    weightValue = Math.round(weightValue * 10) / 10;
  }

  // Construct the final result
  if (weight_unit.toLowerCase() === 'serves') {
    return `${weight_unit} ${weightValue}`;
  } else {
    return `${weightValue} ${weight_unit.toLowerCase()}`;
  }
}

interface BaseParams {
  userDetails?: UserDetails;
  addressData: AddEditAddressUserDetails;
  callbackFn?: (...args: any) => void;
}

interface AddParams extends BaseParams {
  type: 'add';
}

interface UpdateDeleteParams extends BaseParams {
  type: 'update' | 'delete';
  idToUpdateDelete: string;
}

type AddressApiParams = AddParams | UpdateDeleteParams;

export const addressApi = async (
  params: AddressApiParams
): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
    if (params?.userDetails) {
      if (params?.type === 'add') {
        const body = { address: params?.addressData };
        const res = await http.post(ADDRESS_ENDPOINT, body);
        if (res?.response?.ok) {
          return { success: true, data: res?.data };
        } else if (res?.data?.message) {
          return { success: false, error: res?.data?.message };
        }
        return {
          success: false,
          error: 'Some error occurred while updating this address!',
        };
      } else if (params?.type === 'update') {
        const body = { address: params?.addressData };
        const res = await http.put(
          `${ADDRESS_ENDPOINT}/${params?.idToUpdateDelete}`,
          body
        );
        if (res?.response?.ok) {
          return { success: true, data: res?.data };
        } else if (res?.data?.message) {
          return { success: false, error: res?.data?.message };
        }
        return {
          success: false,
          error: 'Some error occurred while updating this address!',
        };
      } else {
        const res = await http.delete(
          `${ADDRESS_ENDPOINT}/${params?.idToUpdateDelete}`
        );
        if (res?.response?.ok) {
          return { success: true, data: res?.data };
        } else if (res?.data?.message) {
          return { success: false, error: res?.data?.message };
        }
        return {
          success: false,
          error: 'Some error occurred while updating this address!',
        };
      }
    }
    return {
      success: false,
      error: 'Some error occurred while updating this address!',
    };
  } catch (e) {
    logger.log('Error in address API', e);
    return {
      success: false,
      error: 'Some error occurred while updating this address!',
    };
  } finally {
    if (params?.callbackFn) {
      params?.callbackFn();
    }
  }
};

export const getRegions = async () => {
  try {
    const res = await http.get(STATES_ENDPOINT, {
      next: { revalidate: REVALIDATE_TIME },
    });
    return res.data.available_regions;
  } catch (error) {
    logger.log('Error while fetching states', error);
    return [];
  }
};

export const updateShippingInformation = async (
  params: UpdateShippingInfromationParams
): Promise<{ success: boolean; response: any }> => {
  try {
    if (!params?.userDetails && params?.cartId) {
      const body = {
        addressInformation: params.addressInformation,
      };
      const res = await http.post(
        `${endpoints.GUEST_CART}/${params?.cartId}/shipping-information`,
        body
      );
      if (res?.response?.ok) {
        return { success: true, response: res?.data };
      } else {
        return { success: false, response: res?.data };
      }
    } else {
      const body = {
        addressInformation: params.addressInformation,
      };
      const res = await http.post(
        `${endpoints.CUSTOMER_CART}/shipping-information`,
        body
      );
      if (res?.response?.ok) {
        return { success: true, response: res?.data };
      } else {
        return { success: false, response: res?.data };
      }
    }
  } catch (e) {
    logger.log('Error while updating Shipping Information of user', e);
    return { success: false, response: e };
  }
};

export const updateBillingInformation = async (
  params: UpdateBillingInfromationParams
): Promise<boolean> => {
  try {
    if (!params?.userDetails && params?.cartId) {
      const body = { address: params.billing_address };
      const res = await http.post(
        `${endpoints.GUEST_CART}/${params?.cartId}/billing-address`,
        body
      );
      if (res?.response?.ok) {
        return true;
      } else {
        return false;
      }
    } else {
      const body = { address: params.billing_address };
      const res = await http.post(
        `${endpoints.CUSTOMER_CART}/billing-address`,
        body
      );
      if (res?.response?.ok) {
        return true;
      } else {
        return false;
      }
    }
  } catch (e) {
    logger.log('Error while updating Shipping Information of user', e);
    return false;
  }
};

export const searchParams = new URLSearchParams(
  typeof window !== 'undefined' ? window.location.search : ''
);

export function cleanAndSplitText(htmlString: string): string[] {
  // Remove only p tags using regular expression
  const cleanedText = htmlString.replace(/<p[^>]*>([^<]+)<\/p>/g, '$1');

  // Split the cleaned text by semicolon
  const splitText = cleanedText.split(';');

  return splitText;
}

export const getFormattedPaymentDetails = (data: any) => {
  return data?.reduce(
    (acc: any, curr: any) => {
      if (curr?.code === 'grand_total') {
        acc.total = curr;
      } else {
        acc.others.push(curr);
      }
      return acc;
    },
    {
      total: null,
      others: [],
    }
  );
};

export function emptyFunction(): void {}

export function convertMinutesToHours(minutes: number) {
  if (isNaN(minutes) || minutes < 0) return 'Invalid input';

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  return hours > 0 && remainingMinutes > 0
    ? `${hours} hr ${remainingMinutes} min`
    : hours > 0
      ? `${hours} hr`
      : `${remainingMinutes} min`;
}

export const segregateGiftingBoxes = (giftingBoxes: any[]) => {
  const largeGifftBoxes = giftingBoxes.filter(
    (box: any) =>
      getValueFromAttributes({
        custom_attributes: box?.custom_attributes,
        code: 'gift_box_size',
      })?.value === 'Large'
  );

  const mediumGifftBoxes = giftingBoxes.filter(
    (box: any) =>
      getValueFromAttributes({
        custom_attributes: box?.custom_attributes,
        code: 'gift_box_size',
      })?.value === 'Medium'
  );

  const smallGifftBoxes = giftingBoxes.filter(
    (box: any) =>
      getValueFromAttributes({
        custom_attributes: box?.custom_attributes,
        code: 'gift_box_size',
      })?.value === 'Small'
  );

  return { largeGifftBoxes, mediumGifftBoxes, smallGifftBoxes };
};

export const normalizePath = (path: string) => path.replace(/\/+$/, '');
/**
 * Replace `/gifting` with `/gifting/all-gifts` in a given path
 * @param path - The input string (URL or pathname)
 * @returns Updated string if `/gifting` found, otherwise original
 */
export const giftingRedirect = (path: string): string => {
  if (!path) return path;
  return path.includes('/gifting')
    ? path.replace('/gifting', '/gifting/all-gifts')
    : path;
};

export const getBentoBoxProducts = async (item: any) => {
  try {
    if (!item) return null;
    const parentSku =
      item?.extension_attributes?.parent_sku || item?.sku || item?.parent_sku;
    if (!parentSku) return null;

    const { data, response } = await http.get(
      `bundle-products/${parentSku}/options/all`,
      { doNotIncludeRegion: true }
    );
    if (response?.ok) return data;
    throw new Error(data?.message || 'Failed to fetch quartet box products');
  } catch (err: any) {
    logger.error(err?.message || err);
    return null;
  }
};

/**
 * Checks if every 'checkbox' type option in the Bento Box map has more than 3 products.
 * Assumes that all products in the list are already filtered for stock availability.
 *
 * @param {BentoBoxProductsType | null} bentoBoxProducts - The map of bento box options and their products.
 * @returns {boolean} - True if all checkbox options have more than 3 available products, otherwise false.
 */
export const hasSufficientProductVariety = (
  bentoBoxProducts: BentoBoxProductsType | null
): boolean => {
  // Return false if the map is null or empty.
  if (!bentoBoxProducts) {
    return false;
  }

  // 1. Get all option values from the map.
  const allOptions = Array.from(bentoBoxProducts.values());

  // 2. Filter to get only the options that are checkboxes.
  const checkboxOptions = allOptions.filter(
    option => option.type === 'checkbox'
  );

  // 3. If there are no checkbox options, the condition is met.
  if (checkboxOptions.length === 0) {
    return false;
  }

  // 4. Use .every() to ensure ALL checkbox options have a product list longer than 3.
  return checkboxOptions.every(option => option.products.length > 3);
};

export type DynamicFilter = {
  key: string;
  [k: string]: any;
};

export async function filterDynamicFiltersByExclusiveList(
  dynamicFilters: DynamicFilter[] = [],
  categoryId?: number
): Promise<DynamicFilter[]> {
  // If either parameter is missing → return as-is
  if (!dynamicFilters.length || !categoryId) {
    return dynamicFilters;
  }

  const EXCLUSIVE_FILTERS_LIST = await getExclusiveFilters();
  const categoryKey = String(categoryId);

  const exclusiveKeysForCategory = EXCLUSIVE_FILTERS_LIST[categoryKey];

  if (exclusiveKeysForCategory) {
    // Category IS exclusive → return only the allowed filters
    const allowed = new Set(exclusiveKeysForCategory);
    return dynamicFilters.filter(f => allowed.has(f.key));
  }

  // Category NOT exclusive → remove all exclusive filters from all categories
  const allExclusiveKeys = new Set(
    Object.values(EXCLUSIVE_FILTERS_LIST).flat()
  );

  return dynamicFilters.filter(f => !allExclusiveKeys.has(f.key));
}
